/*  PatientSpotlightsTab — Admin CRUD for the Patient Spotlights CMS.
    iter100.207

    Manages the long-form quote-block sections on:
      * /how-it-works → "A Patient Story"
      * /pricing      → "The Quoting Experience"

    Each page reads the FIRST published spotlight for its scope. Admins
    can prepare a new spotlight (as unpublished) and flip publish to
    swap the section content live — no deploy required.

    Backend endpoints used:
      GET    /api/admin/spotlights
      POST   /api/admin/spotlights
      PATCH  /api/admin/spotlights/{id}
      DELETE /api/admin/spotlights/{id}
      POST   /api/admin/spotlights/{id}/photo   (multipart)
*/
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Plus, PencilSimple, Trash, Eye, EyeSlash, Upload,
    Quotes, ImageSquare, ArrowsDownUp, Shuffle,
} from "@phosphor-icons/react";


const SCOPE_OPTIONS = [
    { value: "how_it_works", label: "How It Works — A Patient Story",       short: "How It Works" },
    { value: "pricing",      label: "Pricing — The Quoting Experience",     short: "Pricing" },
];

const EMPTY_SPOTLIGHT = {
    id: null,
    scope: "how_it_works",
    name: "",
    quote: "",
    city_meta: "",
    image_url: "",
    badge_top: "",
    badge_bot: "",
    verified_pill_label: "Verified patient",
    outcomes: [
        { value: "", label: "" },
        { value: "", label: "" },
        { value: "", label: "" },
    ],
    display_order: 100,
    is_published: true,
    rotate: false,
};


function SpotlightForm({ open, initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial || EMPTY_SPOTLIGHT);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        // Normalize outcomes to always have 3 rows in the form; strip empties on save.
        const merged = initial ? {
            ...EMPTY_SPOTLIGHT,
            ...initial,
            outcomes: [
                ...(initial.outcomes || []).slice(0, 3),
                ...Array.from({ length: Math.max(0, 3 - (initial.outcomes || []).length) }, () => ({ value: "", label: "" })),
            ],
        } : EMPTY_SPOTLIGHT;
        setForm(merged);
    }, [initial, open]);

    const setOutcome = (idx, key, val) => {
        const next = [...form.outcomes];
        next[idx] = { ...next[idx], [key]: val };
        setForm({ ...form, outcomes: next });
    };

    const save = async () => {
        if (!form.name.trim() || !form.quote.trim()) {
            toast.error("Name and quote are required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                scope: form.scope,
                name: form.name,
                quote: form.quote,
                city_meta: form.city_meta || "",
                image_url: form.image_url || "",
                badge_top: form.badge_top || "",
                badge_bot: form.badge_bot || "",
                verified_pill_label: form.verified_pill_label || "Verified patient",
                outcomes: (form.outcomes || [])
                    .filter((o) => (o.value || "").trim() && (o.label || "").trim())
                    .map((o) => ({ value: o.value, label: o.label })),
                display_order: Number(form.display_order) || 100,
                is_published: !!form.is_published,
                rotate: !!form.rotate,
            };
            if (form.id) {
                await api.patch(`/admin/spotlights/${form.id}`, payload);
                toast.success("Spotlight updated");
            } else {
                await api.post("/admin/spotlights", payload);
                toast.success("Spotlight added");
            }
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const uploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!form.id) {
            toast.error("Save the spotlight first before uploading a photo");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("alt", form.name);
            const r = await api.post(`/admin/spotlights/${form.id}/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm({ ...form, image_url: r.data.image_url });
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const showOutcomes = form.scope === "how_it_works";

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="spotlight-form-dialog">
                <DialogHeader>
                    <DialogTitle>{form.id ? "Edit spotlight" : "Add spotlight"}</DialogTitle>
                    <DialogDescription>
                        Renders on the public page for the chosen scope. Only the FIRST published spotlight per scope is shown.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-1">
                    <div>
                        <Label className="text-xs font-semibold">Scope (which page)</Label>
                        <select
                            value={form.scope}
                            onChange={(e) => setForm({ ...form, scope: e.target.value })}
                            className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-card text-sm"
                            data-testid="spotlight-form-scope"
                        >
                            {SCOPE_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">Patient name</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Aarushi K."
                            className="h-9 text-sm"
                            data-testid="spotlight-form-name"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">City / meta line</Label>
                        <Input
                            value={form.city_meta}
                            onChange={(e) => setForm({ ...form, city_meta: e.target.value })}
                            placeholder={form.scope === "pricing" ? "Bengaluru · Quoted March, completed November" : "Mumbai"}
                            className="h-9 text-sm"
                            data-testid="spotlight-form-meta"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">Quote</Label>
                        <Textarea
                            rows={4}
                            value={form.quote}
                            onChange={(e) => setForm({ ...form, quote: e.target.value })}
                            placeholder="The full patient story…"
                            className="text-sm"
                            data-testid="spotlight-form-quote"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">Photo</Label>
                        <div className="flex items-center gap-3 mt-1">
                            {form.image_url ? (
                                <img src={form.image_url} alt="preview"
                                    className="w-16 h-20 rounded-lg object-cover border border-border" />
                            ) : (
                                <div className="w-16 h-20 rounded-lg bg-secondary grid place-items-center border border-border">
                                    <ImageSquare size={22} className="text-muted-foreground" />
                                </div>
                            )}
                            <div className="flex-1 space-y-1.5">
                                <Input value={form.image_url}
                                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                    placeholder="/marketing/patient.png or paste URL"
                                    className="h-8 text-xs" data-testid="spotlight-form-image-url" />
                                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary cursor-pointer hover:underline">
                                    <Upload size={12} weight="bold" />
                                    {uploading ? "Uploading…" : (form.id ? "Upload photo" : "Save first, then upload")}
                                    <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden"
                                        disabled={!form.id || uploading} data-testid="spotlight-form-photo-input" />
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Badge — top line</Label>
                            <Input value={form.badge_top}
                                onChange={(e) => setForm({ ...form, badge_top: e.target.value })}
                                placeholder={form.scope === "pricing" ? "Quote held to" : "Finished in"}
                                className="h-9 text-sm" data-testid="spotlight-form-badge-top" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Badge — bottom line</Label>
                            <Input value={form.badge_bot}
                                onChange={(e) => setForm({ ...form, badge_bot: e.target.value })}
                                placeholder={form.scope === "pricing" ? "The rupee" : "9 months"}
                                className="h-9 text-sm" data-testid="spotlight-form-badge-bot" />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">Verified pill label</Label>
                        <Input value={form.verified_pill_label}
                            onChange={(e) => setForm({ ...form, verified_pill_label: e.target.value })}
                            placeholder="Verified patient"
                            className="h-9 text-sm" data-testid="spotlight-form-verified" />
                    </div>

                    {showOutcomes && (
                        <div>
                            <Label className="text-xs font-semibold">Outcomes strip <span className="text-muted-foreground font-normal">(shown only on How It Works — up to 3 tiles)</span></Label>
                            <div className="space-y-1.5 mt-1">
                                {form.outcomes.map((o, idx) => (
                                    <div key={idx} className="grid grid-cols-2 gap-2">
                                        <Input value={o.value}
                                            onChange={(e) => setOutcome(idx, "value", e.target.value)}
                                            placeholder={idx === 0 ? "9 months" : idx === 1 ? "24 aligners" : "6 visits"}
                                            className="h-8 text-sm"
                                            data-testid={`spotlight-outcome-value-${idx}`} />
                                        <Input value={o.label}
                                            onChange={(e) => setOutcome(idx, "label", e.target.value)}
                                            placeholder={idx === 0 ? "Total treatment" : idx === 1 ? "Custom trays" : "In-clinic check-ins"}
                                            className="h-8 text-sm"
                                            data-testid={`spotlight-outcome-label-${idx}`} />
                                    </div>
                                ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">Empty rows are dropped on save.</p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Display order</Label>
                            <Input type="number" value={form.display_order}
                                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                                className="h-9 text-sm" data-testid="spotlight-form-order" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Published</Label>
                            <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={form.is_published}
                                    onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                                    data-testid="spotlight-form-published" />
                                <span>{form.is_published ? "Live on site" : "Draft (hidden)"}</span>
                            </label>
                        </div>
                    </div>

                    {/* iter100.209 — A/B rotation flag */}
                    <div className="mt-1 p-3 rounded-xl bg-primary/5 border border-primary/15">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" checked={form.rotate}
                                onChange={(e) => setForm({ ...form, rotate: e.target.checked })}
                                className="mt-0.5"
                                data-testid="spotlight-form-rotate" />
                            <div>
                                <div className="text-xs font-semibold text-foreground">Include in A/B rotation</div>
                                <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug">
                                    When 2+ published spotlights in this scope have rotation on, the site picks one at random per page load. Great for keeping the marketing site feeling fresh.
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={save} disabled={busy} className="rounded-full" data-testid="spotlight-form-submit">
                        {busy ? "Saving…" : (form.id ? "Save changes" : "Add spotlight")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


export default function PatientSpotlightsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterScope, setFilterScope] = useState("all");
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/spotlights")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (filterScope === "all") return items;
        return items.filter((it) => it.scope === filterScope);
    }, [items, filterScope]);

    const scopeCounts = useMemo(() => {
        const c = { all: items.length };
        for (const s of SCOPE_OPTIONS) c[s.value] = 0;
        for (const it of items) c[it.scope] = (c[it.scope] || 0) + 1;
        return c;
    }, [items]);

    const openNew = () => {
        setEditing({ ...EMPTY_SPOTLIGHT, scope: filterScope === "all" ? "how_it_works" : filterScope });
        setShowForm(true);
    };
    const openEdit = (row) => { setEditing(row); setShowForm(true); };
    const togglePub = async (row) => {
        try {
            await api.patch(`/admin/spotlights/${row.id}`, {
                ...row,
                outcomes: row.outcomes || [],
                is_published: !row.is_published,
            });
            toast.success(row.is_published ? "Hidden" : "Published");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Toggle failed");
        }
    };
    const remove = async (row) => {
        try {
            await api.delete(`/admin/spotlights/${row.id}`);
            toast.success("Spotlight removed");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="max-w-4xl" data-testid="patient-spotlights-tab">
            <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                        <Quotes size={20} weight="duotone" className="text-primary" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg font-bold tracking-tight">Patient Spotlights</h3>
                        <p className="text-xs text-muted-foreground mt-0.5 max-w-lg">
                            Long-form quote blocks on <b>/how-it-works</b> and <b>/pricing</b>. Each page renders the first published spotlight for its scope. Prep a new one as Draft, then flip publish to swap live.
                        </p>
                    </div>
                </div>
                <Button size="sm" onClick={openNew} className="rounded-full shrink-0" data-testid="spotlights-add">
                    <Plus size={12} weight="bold" className="mr-1" /> Add spotlight
                </Button>
            </div>

            {/* Scope filter chips */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="spotlights-scope-filter">
                <button
                    onClick={() => setFilterScope("all")}
                    className={`text-[11px] font-semibold px-3 py-1 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                        filterScope === "all"
                            ? "bg-primary/10 border-primary/40 text-primary"
                            : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                    }`}
                    data-testid="spotlights-scope-all"
                >
                    All <span className="text-[10px] bg-muted rounded-full px-1.5 py-0">{scopeCounts.all}</span>
                </button>
                {SCOPE_OPTIONS.map((s) => (
                    <button
                        key={s.value}
                        onClick={() => setFilterScope(s.value)}
                        className={`text-[11px] font-semibold px-3 py-1 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                            filterScope === s.value
                                ? "bg-primary/10 border-primary/40 text-primary"
                                : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                        }`}
                        data-testid={`spotlights-scope-${s.value}`}
                    >
                        {s.short} <span className="text-[10px] bg-muted rounded-full px-1.5 py-0">{scopeCounts[s.value] || 0}</span>
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-xs text-muted-foreground py-10 text-center">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-xl" data-testid="spotlights-empty">
                    No spotlights in this scope yet. Click <b>Add spotlight</b> to create one.
                </div>
            ) : (
                <div className="space-y-2" data-testid="spotlights-list">
                    {filtered.map((row) => {
                        const scopeMeta = SCOPE_OPTIONS.find((s) => s.value === row.scope);
                        return (
                            <div key={row.id} className="flex items-start gap-3 p-3 border border-border rounded-xl bg-card" data-testid={`spotlight-row-${row.id}`}>
                                {row.image_url ? (
                                    <img src={row.image_url} alt={row.name}
                                        className="w-14 h-16 rounded-lg object-cover border border-border shrink-0" />
                                ) : (
                                    <div className="w-14 h-16 rounded-lg bg-secondary grid place-items-center border border-border shrink-0">
                                        <ImageSquare size={18} className="text-muted-foreground" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-sm">{row.name}</span>
                                        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                                            {scopeMeta?.short || row.scope}
                                        </span>
                                        {!row.is_published && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Draft</span>
                                        )}
                                        {row.rotate && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-300"
                                                title="Included in the A/B rotation pool for this scope"
                                                data-testid={`spotlight-rotate-badge-${row.id}`}
                                            >
                                                <Shuffle size={9} weight="bold" /> Rotating
                                            </span>
                                        )}
                                        <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                                            <ArrowsDownUp size={10} /> {row.display_order}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{row.quote}</p>
                                    {row.city_meta && (
                                        <p className="text-[10px] text-muted-foreground/80 mt-0.5">{row.city_meta}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <Button size="sm" variant="ghost" onClick={() => togglePub(row)} className="h-8 px-2"
                                        title={row.is_published ? "Unpublish" : "Publish"}
                                        data-testid={`spotlight-toggle-${row.id}`}>
                                        {row.is_published ? <Eye size={14} /> : <EyeSlash size={14} />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-8 px-2"
                                        data-testid={`spotlight-edit-${row.id}`}>
                                        <PencilSimple size={14} />
                                    </Button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600 hover:bg-rose-50" data-testid={`spotlight-delete-${row.id}`}>
                                                <Trash size={14} />
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Remove spotlight?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Removes &quot;{row.name}&quot; ({scopeMeta?.short || row.scope}) from the public site immediately.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => remove(row)} className="bg-rose-600 hover:bg-rose-700" data-testid={`spotlight-delete-confirm-${row.id}`}>
                                                    Remove
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <SpotlightForm open={showForm} initial={editing} onClose={() => setShowForm(false)} onSaved={load} />
        </div>
    );
}
